/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useCallback } from "react";
import { getBalance, openBox, getOpenBoxResult } from "../api/play";

export function usePlay(userId: string | null) {
  const [balance, setBalance] = useState<number | null>(null);
  const [tokenBalances, setTokenBalances] = useState<Record<string, number>>(
    {}
  );
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openingResult, setOpeningResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────
  // 💰 Fetch balance
  // ─────────────────────────────
  const refreshBalance = useCallback(async () => {
    if (!userId) {
      console.log("[refreshBalance] No userId, skipping");
      return;
    }

    try {
      const data = await getBalance(userId);

      setBalance(data.balance);

      const tokenBalancesMap: Record<string, number> = {};
      data.tokens.forEach((t: any) => {
        tokenBalancesMap[t.token.symbol] = t.balance;
      });

      setTokenBalances(tokenBalancesMap);
    } catch (err) {
      console.error("[refreshBalance] failed:", err);
    }
  }, [userId]);

  // ─────────────────────────────
  // 🎁 Open box
  // ─────────────────────────────
  const startBoxOpening = useCallback(
    async (box: {
      id: string;
      qty: number;
      payment: string;
      onchainRandomNum?: number | null;
      onchainTxHash?: string | null;
    }) => {
      if (!userId) throw new Error("No userId provided");
      const { id, qty, payment, onchainRandomNum, onchainTxHash } = box;
      setLoading(true);
      setOpeningResult(null);
      console.log("Starting box opening...", box);
      try {
        const data = await openBox(
          id,
          userId,
          qty,
          payment,
          onchainRandomNum,
          onchainTxHash
        );
        console.log("Box opening started", data.requestId);
        setOpeningId(data.requestId);
      } catch (err) {
        console.log("Error starting box opening");
        console.error(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  // ─────────────────────────────
  // 🧾 Poll for result
  // ─────────────────────────────
  useEffect(() => {
    if (!openingId) return;
    console.log("Polling for box result...", openingId);

    let retries = 0;
    const maxRetries = 50;
    const interval = setInterval(async () => {
      try {
        const data = await getOpenBoxResult(openingId);
        console.log("Polled box result", data);
        if (data.status === "SUCCESS") {
          console.log("winnerNumbers", data.winnerNumbers);
          setOpeningResult(data); // ✅ store entire data
          setOpeningId(null);
          clearInterval(interval);
          refreshBalance();
        } else if (++retries > maxRetries) {
          clearInterval(interval);
          console.warn("Box result polling timed out");
        }
      } catch (err) {
        console.error("Polling error", err);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [openingId]);

  // ─────────────────────────────
  // Initial balance load
  // ─────────────────────────────
  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  return {
    balance,
    tokenBalances,
    prevOpenings: {
      gamblor: 1,
      astr: 2,
    } as Record<string, number>,
    loading,
    openingId,
    openingResult,
    refreshBalance,
    startBoxOpening,
  };
}
